import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { ListGroupTask } from '@shared/modules/list/model/list-group-task.model';
import { Task } from '@shared/models/task.model';
import { TaskGroup } from '@shared/models/task-group.model';
import {
  ListTasksAccordionToggleEvent,
  ListTasksGroupComponent,
  ListTasksGroupEditEvent,
  ListTasksTemplateAddEvent,
  ListTasksTaskDropEvent,
  ListTasksTaskStatusEvent
} from './components/list-tasks-group/list-tasks-group.component';
import { arrMove } from '@shared/helpers/arr-move.helper';
import { TaskCardLayout } from './task-card-layout.type';

export interface ListTasksGroupOrderEvent {
  draggedGroupId: number;
  groups: Array<{ id: number; order: number }>;
}

@Component({
  selector: 'list-tasks',
  standalone: true,
  templateUrl: './list-tasks.component.html',
  styleUrls: ['./list-tasks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DndModule, ListTasksGroupComponent]
})
export class ListTasksComponent {
  private static readonly ACCORDION_HEADER_TOP_OFFSET = 6;

  groupsInput = input<ListGroupTask[] | null>(null, { alias: 'groups' });
  loading = input<boolean>(false);
  layout = input<TaskCardLayout>('standard');
  disableGroupDrag = input<boolean>(false);
  accordionMode = input<boolean>(false);
  accordionStorageKey = input<string | null>(null);
  accordionMultiExpand = input<boolean>(false);
  accordionAllowSingleGroup = input<boolean>(false);

  taskDropped = output<ListTasksTaskDropEvent>();
  taskStatusChanged = output<ListTasksTaskStatusEvent>();
  taskClicked = output<{ task: Task; groupId: number; isProcessGroup: boolean }>();
  groupEditRequested = output<ListTasksGroupEditEvent>();
  groupOrderChanged = output<ListTasksGroupOrderEvent>();
  addTask = output<ListGroupTask>();
  templateAddSelected = output<ListTasksTemplateAddEvent>();

  protected readonly groupsState = signal<ListGroupTask[]>([]);
  protected readonly expandedGroupId = signal<number | null>(null);
  protected readonly expandedGroupIds = signal<Set<number>>(new Set());
  protected readonly sortedGroups = computed(() =>
    [...this.groupsState()].sort((a, b) => (a.taskGroup?.sortOrder || 0) - (b.taskGroup?.sortOrder || 0))
  );
  protected readonly shouldUseAccordion = computed(
    () => this.accordionMode() && (this.sortedGroups().length > 1 || this.accordionAllowSingleGroup())
  );
  protected readonly isMultiAccordion = computed(() => this.shouldUseAccordion() && this.accordionMultiExpand());

  constructor() {
    effect(
      () => {
        const groups = this.groupsInput();
        this.groupsState.set((groups || []).map((group) => this.normalizeGroup(group)));
        this.restoreAccordionState();
      },
      { allowSignalWrites: true }
    );
  }

  protected onGroupDrop(event: DndDropEvent): void {
    const payload = event.data as { groupId: number } | null;
    if (!payload) return;

    const current = [...this.sortedGroups()];
    const previousIndex = current.findIndex((group) => group.taskGroup?.id === payload.groupId);
    if (previousIndex < 0) return;

    const dropIndex = Number.isInteger(event.index) ? Number(event.index) : current.length;
    const safeIndex = Math.max(0, Math.min(dropIndex, current.length - 1));

    const moved = arrMove(current, previousIndex, safeIndex).map((group, index) => {
      group.taskGroup.sortOrder = index + 1;
      return group;
    });

    this.groupsState.set(moved);
    this.groupOrderChanged.emit({
      draggedGroupId: payload.groupId,
      groups: moved.map((group) => ({ id: group.taskGroup.id, order: group.taskGroup.sortOrder }))
    });
  }

  protected onGroupTaskDrop(event: ListTasksTaskDropEvent): void {
    const state = structuredClone(this.groupsState());
    const sourceGroupIndex = state.findIndex((group) => group.taskGroup.id === event.sourceGroupId);
    const targetGroupIndex = state.findIndex((group) => group.taskGroup.id === event.targetGroupId);
    if (sourceGroupIndex < 0 || targetGroupIndex < 0) return;

    const sourceCollection = this.getCollection(state[sourceGroupIndex], event.sourceStatus);
    const targetCollection = this.getCollection(state[targetGroupIndex], event.targetStatus);

    const sourceTaskIndex = sourceCollection.findIndex((task) => task.id === event.taskId);
    if (sourceTaskIndex < 0) return;

    const [movedTask] = sourceCollection.splice(sourceTaskIndex, 1);
    const safeIndex = Math.max(0, Math.min(event.index ?? targetCollection.length, targetCollection.length));
    targetCollection.splice(safeIndex, 0, movedTask);

    sourceCollection.forEach((task, index) => (task.sortOrder = index + 1));
    targetCollection.forEach((task, index) => (task.sortOrder = index + 1));

    this.groupsState.set(state);
    this.taskDropped.emit(event);
  }

  protected onAddTask(group: ListGroupTask): void {
    this.addTask.emit(group);
  }

  protected onTaskStatusChanged(event: ListTasksTaskStatusEvent): void {
    this.taskStatusChanged.emit(event);
  }

  protected onTaskClicked(event: { task: Task; groupId: number; isProcessGroup: boolean }): void {
    this.taskClicked.emit(event);
  }

  protected onGroupEditRequested(event: ListTasksGroupEditEvent): void {
    this.groupEditRequested.emit(event);
  }

  protected onTemplateAddSelected(event: ListTasksTemplateAddEvent): void {
    this.templateAddSelected.emit(event);
  }

  protected onAccordionToggle(event: ListTasksAccordionToggleEvent): void {
    if (!this.shouldUseAccordion()) return;

    if (this.isMultiAccordion()) {
      const nextExpandedGroupIds = new Set(this.expandedGroupIds());
      const isExpanded = nextExpandedGroupIds.has(event.groupId);
      isExpanded ? nextExpandedGroupIds.delete(event.groupId) : nextExpandedGroupIds.add(event.groupId);

      this.expandedGroupIds.set(nextExpandedGroupIds);
      this.persistAccordionState(Array.from(nextExpandedGroupIds));
      this.restoreAccordionScrollPosition(event.groupId, !isExpanded);
      return;
    }

    const nextExpandedGroupId = this.expandedGroupId() === event.groupId ? null : event.groupId;
    this.expandedGroupId.set(nextExpandedGroupId);
    this.persistAccordionState(nextExpandedGroupId);
    this.restoreAccordionScrollPosition(event.groupId, nextExpandedGroupId !== null);
  }

  protected isGroupExpanded(groupId: number): boolean {
    if (!this.shouldUseAccordion()) return true;
    if (this.isMultiAccordion()) return this.expandedGroupIds().has(groupId);
    return this.expandedGroupId() === groupId;
  }

  private normalizeGroup(group: ListGroupTask): ListGroupTask {
    return {
      ...group,
      taskGroup: new TaskGroup(group.taskGroup || {}),
      tasks: [...(group.tasks || [])],
      completedTasks: [...(group.completedTasks || [])],
      cancelledTasks: [...(group.cancelledTasks || [])]
    };
  }

  private getCollection(group: ListGroupTask, status: 'created' | 'completed' | 'cancelled') {
    if (status === 'completed') return group.completedTasks;
    if (status === 'cancelled') return group.cancelledTasks;
    return group.tasks;
  }

  private restoreAccordionState(): void {
    if (!this.shouldUseAccordion()) {
      this.expandedGroupId.set(null);
      this.expandedGroupIds.set(new Set());
      return;
    }

    const storageKey = this.accordionStorageKey();
    const groupIds = this.sortedGroups()
      .map((group) => Number(group?.taskGroup?.id))
      .filter((id) => Number.isFinite(id));

    if (!groupIds.length) {
      this.expandedGroupId.set(null);
      this.expandedGroupIds.set(new Set());
      return;
    }

    if (this.isMultiAccordion()) {
      this.restoreMultiAccordionState(storageKey, groupIds);
      return;
    }

    if (!storageKey || typeof localStorage === 'undefined') {
      this.expandedGroupId.set(groupIds[0]);
      return;
    }

    const storedValue = localStorage.getItem(storageKey);
    if (storedValue === 'none') {
      this.expandedGroupId.set(null);
      return;
    }

    const storedGroupId = Number(storedValue);
    if (storedValue !== null && groupIds.includes(storedGroupId)) {
      this.expandedGroupId.set(storedGroupId);
      return;
    }

    this.expandedGroupId.set(groupIds[0]);
  }

  private restoreMultiAccordionState(storageKey: string | null, groupIds: number[]): void {
    if (!storageKey || typeof localStorage === 'undefined') {
      this.expandedGroupIds.set(new Set(groupIds));
      return;
    }

    const storedValue = localStorage.getItem(storageKey);
    if (storedValue === 'none') {
      this.expandedGroupIds.set(new Set());
      return;
    }

    try {
      const parsedValue = JSON.parse(storedValue || '[]');
      const expandedIds = Array.isArray(parsedValue)
        ? parsedValue.map((id) => Number(id)).filter((id) => groupIds.includes(id))
        : [];

      this.expandedGroupIds.set(new Set(expandedIds));
      return;
    } catch {
      const storedGroupId = Number(storedValue);
      if (storedValue !== null && groupIds.includes(storedGroupId)) {
        this.expandedGroupIds.set(new Set([storedGroupId]));
        return;
      }
    }

    this.expandedGroupIds.set(new Set(groupIds));
  }

  private persistAccordionState(groupId: number | number[] | null): void {
    const storageKey = this.accordionStorageKey();
    if (!storageKey || typeof localStorage === 'undefined') return;

    if (groupId === null || (Array.isArray(groupId) && !groupId.length)) {
      localStorage.setItem(storageKey, 'none');
      return;
    }

    if (Array.isArray(groupId)) {
      localStorage.setItem(storageKey, JSON.stringify(groupId));
      return;
    }

    localStorage.setItem(storageKey, String(groupId));
  }

  private restoreAccordionScrollPosition(groupId: number, shouldAlignToTop: boolean): void {
    if (typeof document === 'undefined') return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const dashboardContainer = document.querySelector('.dashboard-container') as HTMLElement | null;
        const headerElement = document.querySelector(
          `[data-list-tasks-group-id="${groupId}"] .list-tasks-group__header`
        ) as HTMLElement | null;

        if (!dashboardContainer || !headerElement || !shouldAlignToTop) return;
        if (dashboardContainer.scrollTop <= 0) return;

        const containerRect = dashboardContainer.getBoundingClientRect();
        const headerRect = headerElement.getBoundingClientRect();
        const containerPaddingTop =
          Number.parseFloat(getComputedStyle(dashboardContainer).paddingTop || '0') || 0;
        const deltaWithinContainer =
          headerRect.top -
          containerRect.top -
          containerPaddingTop -
          ListTasksComponent.ACCORDION_HEADER_TOP_OFFSET;

        dashboardContainer.scrollBy({
          top: deltaWithinContainer,
          left: 0,
          behavior: 'auto'
        });
      });
    });
  }
}
